import { useCallback, useEffect, useRef, useState } from 'react'
import {
    ContentSearchParams,
    ContentSearchTaskStatus as ContentSearchTaskStatusEnum,
    ContentView,
    useContentSearchTotalLazyQuery,
    useGetContentSearchTaskLazyQuery,
    useStartContentSearchMutation
} from '../api/client'

// Polling interval in milliseconds
const POLL_INTERVAL_MS = 500

// Threshold in milliseconds after which we show a slow search warning
const SLOW_SEARCH_THRESHOLD_MS = 30000

// Maximum polling duration before timeout (3 minutes)
const MAX_POLL_DURATION_MS = 3 * 60 * 1000

// Maximum consecutive errors before stopping polling
const MAX_CONSECUTIVE_ERRORS = 1

// Re-export the codegen enum for consumers of this hook
export type ContentSearchTaskStatus = ContentSearchTaskStatusEnum

export interface AsyncSearchState {
    // Search progress
    isSearching: boolean
    status: ContentSearchTaskStatus | null
    message: string
    elapsedMs: number
    isSlowSearch: boolean

    // Results - preserved across searches for refetch behavior
    results: ContentView[] | null
    totalCount: number | null
    hasPreviousResults: boolean

    // Total wall-clock duration of the last completed search, measured
    // client-side from the executeSearch call to the response that delivered
    // the results. Because results arrive via polling, this spans the start
    // mutation plus every poll round trip (the duration the user actually
    // experienced, not the server-side elapsedMs the task reports).
    //
    // The results, not the count: the count is a sibling query on its own
    // clock, and timing to whichever of the two landed last would describe
    // neither. — Claude
    //
    // Null while searching, after a failure, or before the first search.
    searchDurationMs: number | null

    // Client wall-clock for the count, stamped when it settles — successfully
    // or not, so that a count still in flight (null) stays distinguishable from
    // one that came back empty. Its own number rather than a share of
    // searchDurationMs because the two requests are genuinely concurrent and
    // routinely differ by two orders of magnitude. — Claude
    countDurationMs: number | null

    // Error & task tracking
    error: string | null
    taskId: string | null
}

export interface UseAsyncContentSearchReturn extends AsyncSearchState {
    // Start new search (preserves previous results during search)
    executeSearch: () => Promise<void>
    // Clear results and totalCount (for filter changes that invalidate everything)
    clearResults: () => void
    // Clear results only, keeping totalCount (for pagination changes)
    clearContent: () => void
}

const initialState: AsyncSearchState = {
    isSearching: false,
    status: null,
    message: '',
    elapsedMs: 0,
    isSlowSearch: false,
    results: null,
    totalCount: null,
    hasPreviousResults: false,
    searchDurationMs: null,
    countDurationMs: null,
    error: null,
    taskId: null
}

export function useAsyncContentSearch(
    params: ContentSearchParams
): UseAsyncContentSearchReturn {
    const [state, setState] = useState<AsyncSearchState>(initialState)

    // Refs for tracking polling state
    const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const elapsedTimerRef = useRef<NodeJS.Timeout | null>(null)
    const searchStartTimeRef = useRef<number | null>(null)
    const currentTaskIdRef = useRef<string | null>(null)
    const consecutiveErrorsRef = useRef<number>(0)
    // Incremented on each executeSearch call. When an awaited mutation resolves, we compare
    // the current generation to the one captured at call time — if they differ, a newer search
    // has been kicked off and this result should be discarded.
    const searchGenerationRef = useRef(0)
    // Indirection so that setTimeout always calls the latest pollTaskStatus closure.
    // Without this, the closure captured by setTimeout would hold stale references to
    // getTaskQuery (which Apollo recreates on every render).
    const pollTaskStatusRef = useRef<() => Promise<void>>(async () => {})

    // GraphQL mutations and queries
    const [startSearchMutation] = useStartContentSearchMutation()
    const [getTaskQuery] = useGetContentSearchTaskLazyQuery({
        fetchPolicy: 'no-cache'
    })
    // The count is a sibling of the search, not a stage of it. The server owns
    // a 10s budget for it and reports 10,002 if it blows it, so this resolves
    // long before a slow search does — where it used to arrive on a later poll
    // of a task that had already finished. no-cache because the paginator must
    // reflect the params actually searched, not a previous run's total.
    // — Claude
    const [runCountQuery] = useContentSearchTotalLazyQuery({
        fetchPolicy: 'no-cache'
    })

    // Stop the local elapsed-time ticker
    const stopElapsedTimer = useCallback(() => {
        if (elapsedTimerRef.current) {
            clearInterval(elapsedTimerRef.current)
            elapsedTimerRef.current = null
        }
    }, [])

    // Start a local 1-second interval that keeps elapsedMs ticking
    const startElapsedTimer = useCallback(() => {
        stopElapsedTimer()
        elapsedTimerRef.current = setInterval(() => {
            const start = searchStartTimeRef.current
            if (start) {
                const ms = Date.now() - start
                setState((prev) => ({
                    ...prev,
                    elapsedMs: ms,
                    isSlowSearch: prev.isSlowSearch || ms > SLOW_SEARCH_THRESHOLD_MS
                }))
            }
        }, 1000)
    }, [stopElapsedTimer])

    // Stop any existing polling
    const stopPolling = useCallback(() => {
        if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current)
            pollingTimeoutRef.current = null
        }
        stopElapsedTimer()
    }, [stopElapsedTimer])

    // Clean up on unmount
    useEffect(() => {
        return () => {
            stopPolling()
        }
    }, [stopPolling])

    // Schedule the next poll after POLL_INTERVAL_MS. Only called when the task is still
    // in progress — terminal states (Completed, Failed, timed-out) call stopPolling instead.
    // Uses setTimeout rather than setInterval to avoid overlapping async calls.
    const scheduleNextPoll = useCallback(() => {
        pollingTimeoutRef.current = setTimeout(() => pollTaskStatusRef.current(), POLL_INTERVAL_MS)
    }, [])

    // Poll for task status
    const pollTaskStatus = useCallback(async () => {
        const taskId = currentTaskIdRef.current
        if (!taskId) return

        // Check for timeout
        const elapsed = searchStartTimeRef.current ? Date.now() - searchStartTimeRef.current : 0

        if (elapsed > MAX_POLL_DURATION_MS) {
            stopPolling()
            setState((prev) => ({
                ...prev,
                isSearching: false,
                status: ContentSearchTaskStatusEnum.Failed,
                error: 'Search timed out. Please try a more specific search.',
                message: 'Search timed out'
            }))
            return
        }

        try {
            const { data, error } = await getTaskQuery({
                variables: { taskId }
            })

            if (error) {
                console.error('Error polling task status:', error)
                stopPolling()
                setState((prev) => ({
                    ...prev,
                    isSearching: false,
                    status: ContentSearchTaskStatusEnum.Failed,
                    error: error.message,
                    message: 'Search failed'
                }))
                return
            }

            const task = data?.getContentSearchTask
            consecutiveErrorsRef.current = 0 // Reset on any successful response

            if (!task) {
                // Task not found - might have expired
                stopPolling()
                setState((prev) => ({
                    ...prev,
                    isSearching: false,
                    status: ContentSearchTaskStatusEnum.Failed,
                    error: 'Search task not found or expired. Please try again.',
                    message: 'Task not found'
                }))
                return
            }

            const taskStatus = task.status

            if (taskStatus === ContentSearchTaskStatusEnum.Completed) {
                // The full request → response duration, as experienced by this
                // client: from executeSearch to the poll that delivered the
                // results.
                const searchDurationMs = searchStartTimeRef.current
                    ? Date.now() - searchStartTimeRef.current
                    : null
                // Results are the task's only deliverable, so this is the end
                // of it. The count is on its own request and settles into
                // `totalCount` independently — a search that finishes first
                // renders with `totalCount === null` until it does. — Claude
                stopPolling()
                setState((prev) => ({
                    ...prev,
                    isSearching: false,
                    status: taskStatus,
                    message: task.message || 'Search completed',
                    isSlowSearch: false,
                    results: (task.results as ContentView[]) || prev.results,
                    hasPreviousResults: false,
                    searchDurationMs,
                    error: null
                }))
            } else if (taskStatus === ContentSearchTaskStatusEnum.Failed) {
                stopPolling()
                setState((prev) => ({
                    ...prev,
                    isSearching: false,
                    status: taskStatus,
                    message: task.message || 'Search failed',
                    isSlowSearch: false,
                    error: task.error || 'An unknown error occurred'
                }))
            } else {
                // Still in progress - schedule next poll
                scheduleNextPoll()
                setState((prev) => ({
                    ...prev,
                    status: taskStatus,
                    message: task.message || 'Searching...'
                }))
            }
        } catch (err) {
            console.error('Error polling task status:', err)
            consecutiveErrorsRef.current += 1

            // Stop polling after too many consecutive errors
            if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
                stopPolling()
                setState((prev) => ({
                    ...prev,
                    isSearching: false,
                    status: ContentSearchTaskStatusEnum.Failed,
                    error: 'Unable to check search status. Please try again.',
                    message: 'Connection error'
                }))
            } else {
                // Retry - schedule next poll
                scheduleNextPoll()
            }
        }
    }, [getTaskQuery, stopPolling, scheduleNextPoll])

    // Keep ref in sync so setTimeout always calls the latest version
    pollTaskStatusRef.current = pollTaskStatus

    // Fire the count for the current params and settle it into state on its own.
    //
    // Deliberately not part of the search's success path: the server caps this
    // at ten seconds and answers 10,002 when it gives up, so it either produces
    // a number the paginator can render or it produces nothing — and in the
    // "nothing" case the results still stand. A count must never be able to
    // fail a search that has rows to show. — Claude
    const runCount = useCallback(
        async (generation: number) => {
            const startedAt = Date.now()
            try {
                const { data, error } = await runCountQuery({ variables: { params } })
                // A newer search has been kicked off — its own count is on the
                // way and this one would overwrite it with the wrong total.
                if (searchGenerationRef.current !== generation) return
                if (error) {
                    console.error('Error counting search results:', error)
                }
                setState((prev) => ({
                    ...prev,
                    totalCount: error ? null : (data?.contentSearchTotal ?? null),
                    countDurationMs: Date.now() - startedAt
                }))
            } catch (err) {
                console.error('Error counting search results:', err)
                if (searchGenerationRef.current !== generation) return
                // Stamped on failure too: it is what lets a readout say "count
                // failed" rather than leaving it looking still in flight
                // forever. — Claude
                setState((prev) => ({ ...prev, totalCount: null, countDurationMs: Date.now() - startedAt }))
            }
        },
        [params, runCountQuery]
    )

    // Execute a new search
    const executeSearch = useCallback(async () => {
        // Stop any existing polling
        stopPolling()

        // Increment generation so any in-flight executeSearch calls become stale
        const generation = ++searchGenerationRef.current

        // Mark as searching, preserving previous results
        setState((prev) => ({
            ...prev,
            isSearching: true,
            status: ContentSearchTaskStatusEnum.Pending,
            message: 'Searching...',
            elapsedMs: 0,
            isSlowSearch: false,
            hasPreviousResults: prev.results !== null,
            searchDurationMs: null,
            countDurationMs: null,
            error: null
        }))

        searchStartTimeRef.current = Date.now()
        consecutiveErrorsRef.current = 0 // Reset error counter for new search
        startElapsedTimer()

        // Started before the mutation below is awaited, so the two are actually
        // in flight together rather than one behind the other. Not awaited:
        // whichever finishes first updates its own slice of state.
        void runCount(generation)

        try {
            const { data, errors } = await startSearchMutation({
                variables: { params }
            })

            // If a newer search was started while we were awaiting, discard this one
            if (searchGenerationRef.current !== generation) return

            if (errors && errors.length > 0) {
                stopPolling()
                setState((prev) => ({
                    ...prev,
                    isSearching: false,
                    status: ContentSearchTaskStatusEnum.Failed,
                    error: errors[0].message,
                    message: 'Failed to start search'
                }))
                return
            }

            const taskId = data?.startContentSearch?.taskId
            if (!taskId) {
                stopPolling()
                setState((prev) => ({
                    ...prev,
                    isSearching: false,
                    status: ContentSearchTaskStatusEnum.Failed,
                    error: 'No task ID returned from server',
                    message: 'Failed to start search'
                }))
                return
            }

            // The backend may return results directly in the mutation response if the search
            // completed within a short server-side wait window, avoiding the need to poll at all.
            const inlineResult = data?.startContentSearch?.result
            if (inlineResult) {
                if (inlineResult.status === 'FAILED') {
                    stopPolling()
                    setState((prev) => ({
                        ...prev,
                        isSearching: false,
                        status: ContentSearchTaskStatusEnum.Failed,
                        message: inlineResult.message || 'Search failed',
                        elapsedMs: inlineResult.elapsedMs || 0,
                        isSlowSearch: false,
                        error: inlineResult.error || 'An unknown error occurred',
                        taskId
                    }))
                    return
                }

                if (inlineResult.status === 'COMPLETED') {
                    // Inline completion: the whole search fitted inside the
                    // start mutation's round trip, so request → response is
                    // just that one call.
                    const searchDurationMs = searchStartTimeRef.current
                        ? Date.now() - searchStartTimeRef.current
                        : null
                    // Same rule as the polling path above: results in hand
                    // means the search is over. Nothing is left to poll for.
                    // — Claude
                    stopPolling()
                    setState((prev) => ({
                        ...prev,
                        isSearching: false,
                        status: ContentSearchTaskStatusEnum.Completed,
                        message: inlineResult.message || 'Search completed',
                        elapsedMs: inlineResult.elapsedMs || 0,
                        isSlowSearch: false,
                        results: (inlineResult.results as ContentView[]) || [],
                        hasPreviousResults: false,
                        searchDurationMs,
                        error: null,
                        taskId
                    }))
                    return
                }
            }

            // No inline result — the search is still running. Store the task ID and poll.
            currentTaskIdRef.current = taskId
            setState((prev) => ({
                ...prev,
                taskId
            }))

            // Poll immediately (pollTaskStatus schedules subsequent polls via setTimeout)
            pollTaskStatusRef.current()
        } catch (err) {
            console.error('Error starting search:', err)
            // Only update state if this is still the current search
            if (searchGenerationRef.current !== generation) return
            stopPolling()
            setState((prev) => ({
                ...prev,
                isSearching: false,
                status: ContentSearchTaskStatusEnum.Failed,
                error: err instanceof Error ? err.message : 'Failed to start search',
                message: 'Failed to start search'
            }))
        }
    }, [params, startSearchMutation, stopPolling, startElapsedTimer, runCount])

    // Clear results and totalCount (for filter changes that invalidate everything)
    const clearResults = useCallback(() => {
        stopPolling()
        setState((prev) => ({
            ...prev,
            results: null,
            totalCount: null,
            hasPreviousResults: false
        }))
    }, [stopPolling])

    // Clear results only, keeping totalCount (for pagination changes)
    const clearContent = useCallback(() => {
        stopPolling()
        setState((prev) => ({
            ...prev,
            results: null,
            hasPreviousResults: false
        }))
    }, [stopPolling])

    return {
        ...state,
        executeSearch,
        clearResults,
        clearContent
    }
}

export default useAsyncContentSearch
